import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  collection,
  getDocs,
  getFirestore,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";

export const getPremiumStatus = async (app) => {
  const auth = getAuth(app);
  const userId = auth.currentUser?.uid;
  if (!userId) throw new Error("User not logged in");

  const db = getFirestore(app);
  const usersRef = collection(db, "users");
  const q = query(usersRef, where("authUID", "==", userId));

  try {
    const snapshot = await getDocs(q);
    if (!snapshot.empty) {
      const pickBestDoc = (docs) => {
        if (docs.length === 1) return docs[0];
        const real = docs.filter(
          (d) => !String(d.data().displayPhoto || "").includes("placeholder.com")
        );
        const pool = real.length > 0 ? real : docs;
        return pool.reduce((best, curr) =>
          Object.keys(curr.data()).length > Object.keys(best.data()).length
            ? curr
            : best
        );
      };
      return pickBestDoc(snapshot.docs).data().isPremium === true;
    }
  } catch (e) {
    console.error("Failed to fetch premium status:", e);
  }
  return false;
};

/**
 * Normalizes any timestamp representation (Firestore Timestamp, Date, string, number) to epoch milliseconds.
 */
export const normalizeTimestampMs = (ts) => {
  if (!ts) return null;
  if (typeof ts === "number") return ts;
  if (ts.toMillis && typeof ts.toMillis === "function") return ts.toMillis();
  if (typeof ts.seconds === "number") return ts.seconds * 1000 + (ts.nanoseconds ? Math.floor(ts.nanoseconds / 1000000) : 0);
  if (ts instanceof Date) return ts.getTime();
  if (typeof ts === "string") {
    const parsed = new Date(ts).getTime();
    return isNaN(parsed) ? null : parsed;
  }
  return null;
};

/**
 * Parses user document data and returns the active entitlement object if unexpired.
 * Accurately treats cancelled subscriptions with unexpired access as active entitlements.
 */
export const parseSubscriptionDetails = (userData) => {
  if (!userData) return null;
  const now = Date.now();

  // 0. Canonical premiumEntitlement (Account-level Source of Truth)
  const canon = userData.premiumEntitlement;
  if (canon && typeof canon === "object") {
    const isRevokedOrRefunded = canon.status === "refunded" || canon.status === "revoked";
    const expiresMs = normalizeTimestampMs(canon.expiresAt);
    const isUnexpired = expiresMs ? expiresMs > now : (canon.plan === "lifetime" || canon.status === "active");

    if (canon.active === true && isUnexpired && !isRevokedOrRefunded) {
      const rawProv = String(canon.provider || "").toLowerCase();
      const provider = rawProv.includes("apple") ? "apple_app_store"
        : rawProv.includes("google") || rawProv.includes("android") ? "google_play"
        : rawProv === "lifetime" ? "lifetime"
        : "stripe";
      const type = canon.type || (provider === "apple_app_store" ? "applePay" : provider === "google_play" ? "googlePay" : "stripe");
      let plan = "unknown";
      const rawPlan = String(canon.plan || "").toLowerCase();
      if (rawPlan.includes("week")) plan = "weekly";
      else if (rawPlan.includes("month")) plan = "monthly";
      else if (rawPlan.includes("year") || rawPlan.includes("annual")) plan = "yearly";
      else if (rawPlan === "lifetime") plan = "lifetime";

      return {
        ...canon,
        provider,
        type,
        plan,
        status: canon.status || "active",
        autoRenew: canon.autoRenew === true,
        expiresAt: expiresMs,
        expiresDate: expiresMs,
        isActive: true,
      };
    }
  }

  // 1. Lifetime grant
  if (userData.isPremium === true && userData.premiumSource === 'lifetime') {
    return {
      provider: 'lifetime',
      type: 'lifetime',
      plan: 'lifetime',
      status: 'active',
      autoRenew: false,
      expiresAt: null,
      isActive: true,
    };
  }

  // 2. Apple App Store subscription
  const apple = userData.appleSubscription;
  if (apple) {
    const expiresMs = normalizeTimestampMs(apple.expiresDate || apple.expiryDate || apple.expiryTimeMillis || apple.expires_date);
    const isRevokedOrRefunded = apple.status === 'refunded' || apple.status === 'revoked';

    if ((expiresMs && expiresMs > now && !isRevokedOrRefunded) || (userData.isPremium && apple.status === 'active' && !isRevokedOrRefunded)) {
      let plan = 'unknown';
      const pid = (apple.productId || apple.subscriptionId || apple.plan || apple.id || '').toLowerCase();
      if (pid.includes('weekly') || pid.includes('week')) plan = 'weekly';
      else if (pid.includes('monthly') || pid.includes('month')) plan = 'monthly';
      else if (pid.includes('yearly') || pid.includes('year') || pid.includes('annual')) plan = 'yearly';
      else if (userData.plan) {
        const up = String(userData.plan).toLowerCase();
        if (up.includes('weekly') || up.includes('week')) plan = 'weekly';
        else if (up.includes('monthly') || up.includes('month')) plan = 'monthly';
        else if (up.includes('yearly') || up.includes('year') || up.includes('annual')) plan = 'yearly';
      }

      return {
        ...apple,
        provider: 'apple_app_store',
        type: 'applePay',
        plan,
        productId: apple.productId || pid,
        expiresDate: expiresMs,
        expiresAt: expiresMs,
        autoRenew: apple.autoRenewStatus === true || apple.autoRenew === true,
        status: apple.status || 'active',
        isActive: true,
      };
    }
  }

  // 3. Google Play subscription
  const android = userData.androidSubscription;
  if (android) {
    const expiryMs = normalizeTimestampMs(android.expiryTimeMillis || android.expiresDate || android.expiryDate || android.expires_date);
    const isRevokedOrRefunded = android.status === 'refunded' || android.status === 'revoked';

    if ((expiryMs && expiryMs > now && !isRevokedOrRefunded) || (userData.isPremium && android.status === 'active' && !isRevokedOrRefunded)) {
      let plan = 'unknown';
      const sid = (android.subscriptionId || android.productId || android.plan || android.id || '').toLowerCase();
      if (sid.includes('weekly') || sid.includes('week')) plan = 'weekly';
      else if (sid.includes('monthly') || sid.includes('month')) plan = 'monthly';
      else if (sid.includes('yearly') || sid.includes('year') || sid.includes('annual')) plan = 'yearly';
      else if (userData.plan) {
        const up = String(userData.plan).toLowerCase();
        if (up.includes('weekly') || up.includes('week')) plan = 'weekly';
        else if (up.includes('monthly') || up.includes('month')) plan = 'monthly';
        else if (up.includes('yearly') || up.includes('year') || up.includes('annual')) plan = 'yearly';
      }

      return {
        ...android,
        provider: 'google_play',
        type: 'googlePay',
        plan,
        subscriptionId: android.subscriptionId || sid,
        expiryTimeMillis: expiryMs,
        expiresAt: expiryMs,
        autoRenew: android.autoRenewing === true || android.autoRenew === true,
        status: android.status || 'active',
        isActive: true,
      };
    }
  }

  // 4. Stripe subscription
  const stripeSub = userData.subscription;
  if (stripeSub && stripeSub.type !== 'applePay' && stripeSub.type !== 'googlePay') {
    const periodEndMs = normalizeTimestampMs(stripeSub.currentPeriodEnd || stripeSub.current_period_end || stripeSub.expiresDate);
    const isRevokedOrRefunded = stripeSub.status === 'incomplete_expired' || stripeSub.status === 'refunded';
    const isStatusActive = stripeSub.status === 'active' || stripeSub.status === 'trialing';
    const isUnexpired = periodEndMs ? periodEndMs > now : isStatusActive;

    if (isUnexpired && !isRevokedOrRefunded) {
      let plan = 'unknown';
      const pid = (stripeSub.priceId || stripeSub.plan || stripeSub.id || '').toLowerCase();
      const interval = (stripeSub.interval || '').toLowerCase();

      if (interval.includes('week') || pid.includes('weekly') || pid.includes('week')) plan = 'weekly';
      else if (interval.includes('month') || pid.includes('monthly') || pid.includes('month')) plan = 'monthly';
      else if (interval.includes('year') || interval.includes('annual') || pid.includes('yearly') || pid.includes('year') || pid.includes('annual')) plan = 'yearly';
      else if (userData.plan) {
        const up = String(userData.plan).toLowerCase();
        if (up.includes('weekly') || up.includes('week')) plan = 'weekly';
        else if (up.includes('monthly') || up.includes('month')) plan = 'monthly';
        else if (up.includes('yearly') || up.includes('year') || up.includes('annual')) plan = 'yearly';
      }

      return {
        ...stripeSub,
        provider: 'stripe',
        type: 'stripe',
        plan,
        interval: plan,
        currentPeriodEnd: periodEndMs,
        expiresAt: periodEndMs,
        autoRenew: !stripeSub.cancelAtPeriodEnd && !stripeSub.cancel_at_period_end,
        status: stripeSub.status || (stripeSub.cancelAtPeriodEnd ? 'cancelled' : 'active'),
        isActive: true,
      };
    }
  }

  // 5. Fallback for user documents where isPremium is true with premiumSource
  if (userData.isPremium === true) {
    let plan = 'unknown';
    const rawPlan = (userData.plan || userData.subscriptionPlan || userData.subscriptionTier || '').toLowerCase();
    if (rawPlan.includes('weekly') || rawPlan.includes('week')) plan = 'weekly';
    else if (rawPlan.includes('monthly') || rawPlan.includes('month')) plan = 'monthly';
    else if (rawPlan.includes('yearly') || rawPlan.includes('year') || rawPlan.includes('annual')) plan = 'yearly';

    const provider = userData.premiumSource === 'android' ? 'google_play'
      : userData.premiumSource === 'apple' ? 'apple_app_store'
      : userData.premiumSource === 'stripe' ? 'stripe'
      : userData.premiumSource || 'premium';

    const type = userData.premiumSource === 'android' ? 'googlePay'
      : userData.premiumSource === 'apple' ? 'applePay'
      : userData.premiumSource === 'stripe' ? 'stripe'
      : userData.premiumSource || 'premium';

    return {
      provider,
      type,
      plan,
      status: 'active',
      autoRenew: true,
      expiresAt: null,
      isActive: true,
    };
  }

  return null;
};

/**
 * Returns the normalized active plan identifier ('weekly', 'monthly', 'yearly', 'lifetime') or null.
 */
export const getActivePlan = (userData) => {
  const details = parseSubscriptionDetails(userData);
  return details ? details.plan : null;
};

export const getSubscriptionDetails = async (app) => {
  const auth = getAuth(app);
  const userId = auth.currentUser?.uid;
  if (!userId) throw new Error("User not logged in");

  const db = getFirestore(app);
  const usersRef = collection(db, "users");
  const q = query(usersRef, where("authUID", "==", userId));

  try {
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;

    const pickBestDoc = (docs) => {
      if (docs.length === 1) return docs[0];
      const real = docs.filter(
        (d) => !String(d.data().displayPhoto || "").includes("placeholder.com")
      );
      const pool = real.length > 0 ? real : docs;
      return pool.reduce((best, curr) =>
        Object.keys(curr.data()).length > Object.keys(best.data()).length
          ? curr
          : best
      );
    };
    const userData = pickBestDoc(snapshot.docs).data();
    return parseSubscriptionDetails(userData);
  } catch (e) {
    console.error("Failed to fetch subscription details:", e);
  }

  return null;
};

export const startPremiumStatusSync = (app) => {
  const auth = getAuth(app);
  const db = getFirestore(app);

  let stopListener = null;

  const stopAuthListener = onAuthStateChanged(auth, (user) => {

    if (typeof stopListener === "function") {
      stopListener();
      stopListener = null;
    }
    if (!user) return;

    const userId = user.uid;
    const usersRef = collection(db, "users");
    const q = query(usersRef, where("authUID", "==", userId));

    stopListener = onSnapshot(q, (snapshot) => {
      if (!snapshot.empty) {
        const pickBestDoc = (docs) => {
          if (docs.length === 1) return docs[0];
          const real = docs.filter(
            (d) => !String(d.data().displayPhoto || "").includes("placeholder.com")
          );
          const pool = real.length > 0 ? real : docs;
          return pool.reduce((best, curr) =>
            Object.keys(curr.data()).length > Object.keys(best.data()).length
              ? curr
              : best
          );
        };
        const isPremium = pickBestDoc(snapshot.docs).data().isPremium === true;
        // This listener ensures we are subscribed to updates, 
        // even if we don't dispatch them globally here.
        // Components using getPremiumStatus will get the latest value on re-render
        // or one could perform a callback here if needed.
        console.log("Realtime premium status sync:", isPremium);
      }
    }, (error) => {
      console.error("Premium sync listener failed:", error);
    });
  });

  return () => {
    if (typeof stopListener === "function") stopListener();
    stopAuthListener();
  };
};
